import { TextButton } from '@creditornot/cb-button';
import { InputContainer } from '@creditornot/cb-form';
import { InfoCircleFilled } from '@creditornot/cb-icons';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { Input, InputAlert } from '@creditornot/cb-input';
import { Popover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { equals, isEmpty, isNil } from 'ramda';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { getProductFromSource } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { NutritionSetting } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { RFFFieldErrorWrapper } from 'common/components';

import { FeedFormValues, FormValues } from './types';
import { transformFeedProductToFormValues } from '../../transformers';

type Props = {
  menuId: string;
  country: string;
  primaryLanguage: Language;
  listedFieldNames: string[];
  nutritionSettings: NutritionSetting[];
  onChangeNutritionFactsFormat: (nutritionFactsFormat: 'text' | 'table') => void;
};

const Root = styled.div``;

const StyledInputContainer = styled(InputContainer)`
  max-width: 392px;
`;

const AdvancedItemDetailsText = styled.div`
  max-width: 216px;
  ${typographyCss.Body3};
`;

const FetchContainer = styled.div`
  margin-block-start: 10px;
`;

const FetchSpinner = styled(Spinner).attrs({ size: 'tiny' })`
  margin-inline-start: 8px;
`;

const InfoIcon = styled(InfoCircleFilled).attrs({
  width: 12,
  height: 12,
})`
  margin: 0 8px;
  cursor: pointer;
  fill: ${cbSemanticCssVars.textPrimary};
`;

type DisconnectButtonProps = {
  hasMargin: boolean;
};

const DisconnectButton = styled(TextButton)<DisconnectButtonProps>`
  margin-inline-start: ${props => (props.hasMargin ? '4px' : '0')};
`;

const WarningAlert = styled(InputAlert).attrs({ variant: 'warning' })`
  margin-block-start: 10px;
  margin-block-end: 10px;
`;

const ErrorAlert = styled(InputAlert).attrs({ variant: 'error' })`
  margin-block-start: 8px;
`;

const SyncedFieldsList = styled.ul`
  margin: 4px 0 0;
  padding-block-end: 0;
  padding-inline-start: 24px;
`;

const fieldToTranslationKeyMap: Record<keyof FeedFormValues, string | null> = {
  additives: 'menu.additives',
  alcoholPercentage: 'menu-item.alcoholPercentage',
  allergens: 'menu.allergens',
  brandId: 'menu-item.brand',
  description: 'menu-item.description',
  dietaryPreferences: 'menu.dietaryPreferences.title',
  distributorInfo: 'menu.distributor-information',
  image: 'menu-item.image',
  ingredients: 'menu.ingredients',
  mandatoryWarnings: 'menu.mandatoryWarnings.title',
  moreInformation: 'menu.more-information',
  name: 'menu-item.name',
  numberOfUnits: 'menu-item.quantity',
  nutritionFacts: 'menu.nutrition-facts',
  nutritionValuesBaseline: 'menu.nutrition-values-baseline',
  nutritionValues: 'menu.nutrition-values',
  countryOfOrigin: 'menu.country-of-origin',
  usageInstructions: 'menu.usage-instructions',
  producerInfo: 'menu.producer-information',
  volumeInMl: 'menu-item.volume',
  weightInGrams: 'menu-item.weight',
  displayUnit: 'menu-item.display-unit',
  productHierarchyLevelId: 'menu-item.product-family',
};

const immutableFields: (keyof FeedFormValues)[] = ['name', 'productHierarchyLevelId'];

export const getFeedFormChanges = (formValues: FormValues, feedData: Partial<FeedFormValues>) => {
  // TODO: check if this works for VAT
  const comparableFormValues: FormValues = {
    ...formValues,
    nutritionValues: formValues.nutritionValues.filter(v => v.value !== undefined),
  };
  const formChanges = Object.keys(feedData).reduce((acc: Partial<FormValues>, curr: keyof FeedFormValues) => {
    if (curr === 'image') {
      if ((!feedData[curr] || feedData[curr]?.length === 0) && formValues.image.length !== 0) {
        return { ...acc, sourceConfig: { ...acc.sourceConfig, image: 'USER_INPUT' } };
      }
      return {
        ...acc,
        image: feedData.image?.map(i => ({ type: 'existing-image', imageUrl: i.id, id: i.id })) ?? [],
      };
    }
    const currentValueIsEmpty = isEmpty(comparableFormValues[curr]) || isNil(comparableFormValues[curr]);
    if (immutableFields.includes(curr) && !currentValueIsEmpty) {
      return {
        ...acc,
        [curr]: comparableFormValues[curr],
        sourceConfig: { ...acc.sourceConfig, [curr]: 'USER_INPUT' },
      };
    }
    const catalogValueIsEmpty = isEmpty(feedData[curr]) || isNil(feedData[curr]);
    if (catalogValueIsEmpty && !equals(feedData[curr], comparableFormValues[curr])) {
      return curr === 'nutritionFacts' || curr === 'nutritionValues' || curr === 'nutritionValuesBaseline'
        ? { ...acc, sourceConfig: { ...acc.sourceConfig, nutritionFields: 'USER_INPUT' } }
        : { ...acc, sourceConfig: { ...acc.sourceConfig, [curr]: 'USER_INPUT' } };
    }
    return { ...acc, [curr]: feedData[curr] };
  }, {});
  return { ...formChanges, sourceConfig: formChanges.sourceConfig || {} };
};

const mapError = (error: unknown): string => {
  if (!isAxiosError(error)) {
    return 'Unexpected error';
  } else if (error.response?.status === 404) {
    return error.response?.data.msg || 'Product not found';
  } else {
    return error.message;
  }
};

const isFeedFormKey = (key: string): key is keyof FeedFormValues => Object.keys(fieldToTranslationKeyMap).includes(key);

const FeedField = ({
  menuId,
  country,
  primaryLanguage,
  listedFieldNames,
  nutritionSettings,
  onChangeNutritionFactsFormat,
}: Props) => {
  const intl = useIntl();
  const form = useForm<FormValues>();
  const { values, submitting } = useFormState<FormValues>();
  const barcodeIsDefined = !!values.barcodeGtin && values.barcodeGtin.length > 0;
  const isConnected = !!values.sourceCountry;

  const {
    data: productInformation,
    isLoading: isLoadingProductInformation,
    error: fetchProductInformationError,
    mutate: fetchProductInformation,
    reset: resetProductionInformationFetch,
  } = useMutation(async () => {
    if (!values.barcodeGtin) {
      return;
    }
    const product = await getProductFromSource(menuId, values.barcodeGtin, country);
    const transformedData = transformFeedProductToFormValues(product, { language: primaryLanguage, nutritionSettings });
    const mappedData = listedFieldNames.reduce<Partial<FeedFormValues>>(
      (acc, curr) => (isFeedFormKey(curr) ? { ...acc, [curr]: transformedData[curr] } : acc),
      {},
    );
    const formChanges = getFeedFormChanges(values, mappedData);
    form.batch(() => {
      form.change('sourceCountry', country);
      Object.keys(formChanges).forEach((field: keyof FormValues) => {
        form.change(field, formChanges[field] as FormValues[typeof field]);
      });
    });
    onChangeNutritionFactsFormat(
      mappedData.nutritionValues?.some(value => value.value !== undefined) ? 'table' : 'text',
    );
    return product;
  });

  const hasLanguageMismatch = useMemo(
    () => (productInformation ? !productInformation.name.map(name => name.lang).includes(primaryLanguage) : false),
    [primaryLanguage, productInformation],
  );

  // If user changes the GTIN after the previous fetch has failed, clear the fetch error
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => resetProductionInformationFetch(), [values.barcodeGtin]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        fetchProductInformation();
      }
    },
    [fetchProductInformation],
  );

  return (
    <Field<FormValues['barcodeGtin']> name="barcodeGtin">
      {field => (
        <Root>
          <RFFFieldErrorWrapper field={field}>
            {input => (
              <StyledInputContainer>
                <Input
                  {...input}
                  disabled={isConnected || isLoadingProductInformation}
                  onKeyDown={handleKeyDown}
                  placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                />
              </StyledInputContainer>
            )}
          </RFFFieldErrorWrapper>
          {!!fetchProductInformationError && (
            <ErrorAlert>
              <FormattedMessage id="menu.catalog-product-loading-error" />
              {mapError(fetchProductInformationError)}
            </ErrorAlert>
          )}
          <FetchContainer>
            {!isConnected && (
              <>
                <TextButton
                  data-testid="feed_connect_button"
                  disabled={submitting || field.meta.invalid || !barcodeIsDefined}
                  onClick={() => {
                    fetchProductInformation();
                  }}
                >
                  <FormattedMessage id="menu.sync-with-feed-data" />
                </TextButton>
                {isLoadingProductInformation && <FetchSpinner />}
              </>
            )}
            {isConnected && (
              <>
                {hasLanguageMismatch ? (
                  <WarningAlert>
                    <FormattedMessage id="menu.feed-connected-help-text-language-warning" />
                  </WarningAlert>
                ) : (
                  <FormattedMessage id="menu.feed-connected-help-text" />
                )}
                <DisconnectButton
                  hasMargin={!hasLanguageMismatch}
                  disabled={submitting}
                  onClick={() => form.change('sourceCountry', undefined)}
                >
                  <FormattedMessage id="menu.unsync-with-feed-data" />
                </DisconnectButton>
              </>
            )}
            <Popover
              renderArrow={false}
              showOnClick={false}
              content={
                <AdvancedItemDetailsText>
                  <FormattedMessage id="menu-item.sync-feed-help-text" />
                  <SyncedFieldsList>
                    {listedFieldNames.map((field: keyof FeedFormValues) => {
                      const translationKey = fieldToTranslationKeyMap[field];
                      return translationKey ? (
                        <li key={field}>
                          <FormattedMessage id={translationKey} />
                        </li>
                      ) : null;
                    })}
                  </SyncedFieldsList>
                </AdvancedItemDetailsText>
              }
              placement={isConnected ? 'bottom-start' : 'right-end'}
            >
              <InfoIcon />
            </Popover>
          </FetchContainer>
        </Root>
      )}
    </Field>
  );
};

export default FeedField;
